import { VendedorQuota, CotaDiaria, DataProductionRecord } from '../types/wizard';

/**
 * Calculate daily quotas for vendors based on production data and vendor shares
 */
export const calculateDailyQuotas = (
  vendorQuotas: VendedorQuota[],
  productionData: DataProductionRecord[],
  filial: string,
  year: number,
  month: number
): CotaDiaria[] => {
  console.log('🔢 calculateDailyQuotas called with:', { 
    vendorQuotas, 
    filial, 
    year, 
    month,
    productionDataLength: productionData.length 
  });
  
  const dailyQuotas: CotaDiaria[] = [];

  // Create a map of production by date for faster lookup
  const productionByDate = new Map<string, number>();
  productionData.forEach(record => {
    const existing = productionByDate.get(record.date) || 0;
    productionByDate.set(record.date, existing + record.production);
  });

  // Generate dates for the month (1-31)
  // Note: month parameter is 1-indexed (1=Jan, 7=July, 12=Dec)
  // new Date(year, month, 0) gets last day of month (month is treated as 1-indexed here)
  const daysInMonth = new Date(year, month, 0).getDate();
  
  for (let day = 1; day <= daysInMonth; day++) {
    const date = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    const dailyVolume = productionByDate.get(date) || 0;

    // Calculate quota for each vendor (even if production is 0)
    vendorQuotas.forEach(vendor => {
      // Only include vendors with valid name and percentage > 0
      const hasValidName = vendor.vendedor && vendor.vendedor.trim().length > 0;
      const hasValidPercentage = vendor.fatia_percentual > 0;
      
      console.log(`🔢 Processing vendor: "${vendor.vendedor}" | Percentage: ${vendor.fatia_percentual} | Valid: ${hasValidName && hasValidPercentage}`);
      
      if (hasValidName && hasValidPercentage) {
        const cotaDiaria = Math.round((vendor.fatia_percentual / 100) * dailyVolume * 100) / 100; // Round to 2 decimal places
        
        dailyQuotas.push({
          no_vendedor: vendor.vendedor,
          filial: filial,
          data: date,
          cota: cotaDiaria
        });
      } else {
        console.log(`⚠️ Skipping vendor: "${vendor.vendedor}" | Reason: ${!hasValidName ? 'invalid name' : 'zero percentage'}`);
      }
    });
  }

  console.log(`🔢 calculateDailyQuotas result: ${dailyQuotas.length} quotas generated for ${vendorQuotas.filter(v => v.vendedor && v.vendedor.trim() && v.fatia_percentual > 0).length} valid vendors`);
  
  // Log vendor summary
  const vendorSummary = vendorQuotas.map(v => `${v.vendedor}: ${v.fatia_percentual}%`).join(', ');
  console.log(`🔢 Vendor summary: ${vendorSummary}`);

  return dailyQuotas;
};

/**
 * Validate daily quotas calculation
 */
export const validateDailyQuotas = (
  vendorQuotas: VendedorQuota[],
  productionData: DataProductionRecord[],
  year: number,
  month: number
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Check if we have vendor quotas
  if (vendorQuotas.length === 0) {
    errors.push('Nenhum vendedor configurado');
  }

  // Check if total percentage equals 100%
  const totalPercentage = vendorQuotas.reduce((sum, vendor) => sum + vendor.fatia_percentual, 0);
  if (Math.abs(totalPercentage - 100) > 0.01) {
    errors.push(`Soma das fatias deve ser 100%. Atual: ${totalPercentage.toFixed(2)}%`);
  }

  // Check if we have production data
  if (productionData.length === 0) {
    errors.push('Nenhum dado de produção encontrado');
  }

  // Check if we have production data for the selected month
  const monthStr = `${year}-${month.toString().padStart(2, '0')}`;
  const hasDataForMonth = productionData.some(record => record.date.startsWith(monthStr));
  if (!hasDataForMonth) {
    errors.push(`Nenhum dado de produção encontrado para ${month}/${year}`);
  }

  // Check for days with missing production data (note: days with 0 production are valid)
  const daysInMonth = new Date(year, month, 0).getDate();
  const productionByDate = new Map<string, number>();
  productionData.forEach(record => {
    const existing = productionByDate.get(record.date) || 0;
    productionByDate.set(record.date, existing + record.production);
  });

  const daysWithData = productionByDate.size;
  if (daysWithData === 0) {
    errors.push('Nenhum dia com dados de produção encontrado no mês');
  } else if (daysWithData < daysInMonth * 0.5) {
    // Only warn if less than 50% of days have production data
    errors.push(`Poucos dias com produção: ${daysWithData}/${daysInMonth} dias`);
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Get production volume for a specific date
 */
export const getProductionVolumeForDate = (
  productionData: DataProductionRecord[],
  date: string
): number => {
  return productionData
    .filter(record => record.date === date)
    .reduce((total, record) => total + record.production, 0);
};

/**
 * Get summary of daily quotas by vendor
 */
export const getDailyQuotaSummary = (dailyQuotas: CotaDiaria[]) => {
  const summary = new Map<string, { totalQuota: number; days: number }>();

  dailyQuotas.forEach(quota => {
    const existing = summary.get(quota.no_vendedor) || { totalQuota: 0, days: 0 };
    summary.set(quota.no_vendedor, {
      totalQuota: existing.totalQuota + quota.cota,
      days: existing.days + 1
    });
  });

  return Array.from(summary.entries()).map(([vendedor, data]) => ({
    vendedor,
    totalQuota: Math.round(data.totalQuota * 100) / 100,
    averageDaily: Math.round((data.totalQuota / data.days) * 100) / 100,
    days: data.days
  }));
};